import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchOrderSuggestions, fetchOrderSummary, fetchOrders } from '../../api/orders';
import { fetchBranches } from '../../api/salon';
import OrderDetailDrawer from '../../components/orders/OrderDetailDrawer';
import DashboardShell from '../../components/ui/DashboardShell';
import DatePicker from '../../components/ui/DatePicker';
import EmptyState from '../../components/ui/EmptyState';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { CardSkeleton, TableRowSkeleton } from '../../components/ui/Skeleton';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';

const ORDER_STATUS_OPTIONS = ['PENDING', 'CONFIRMED', 'PACKING', 'SHIPPED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'REJECTED'];

const SalonOrdersPage = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('');
  const [branchId, setBranchId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['order-suggestions', status, branchId],
    fetchSuggestions: (q) =>
      fetchOrderSuggestions({
        q,
        status: status || undefined,
      }),
  });

  const paymentBadge = (order) => {
    const verified = order.paymentSlips?.some((s) => s.isVerified);
    if (verified) return { label: t('orders.payment.verified'), className: 'text-emerald-400' };
    if (order.paymentSlips?.length) return { label: t('orders.payment.slipUploaded'), className: 'text-amber-400' };
    return { label: t('orders.payment.awaiting'), className: 'text-zinc-600 dark:text-zinc-500' };
  };

  const { data: summary } = useQuery({
    queryKey: ['order-summary'],
    queryFn: fetchOrderSummary,
  });

  const { data: branchData } = useQuery({
    queryKey: ['branches'],
    queryFn: fetchBranches,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['orders', page, pageSize, status, branchId, dateFrom, dateTo, appliedSearch],
    queryFn: () =>
      fetchOrders({
        page,
        limit: pageSize,
        status: status || undefined,
        branchId: branchId || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: appliedSearch || undefined,
      }),
  });

  const orders = data?.orders || [];
  const pagination = data?.pagination;
  const branches = branchData?.branches || [];

  const summaryCards = [
    { label: t('orders.salonSummary.pending'), value: summary?.pending },
    { label: t('orders.salonSummary.confirmed'), value: summary?.confirmed },
    { label: t('orders.salonSummary.inProgress'), value: summary?.inProgress },
    { label: t('orders.salonSummary.deliveredToday'), value: summary?.deliveredToday },
  ];

  return (
    <DashboardShell title={t('orders.title')} subtitle={t('orders.subtitle')}>
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
            <p className="text-xs text-zinc-600 dark:text-zinc-500">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value ?? '—'}</p>
          </div>
        ))}
      </div>

      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('orders.filters.searchClient')}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
      >
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
          <option value="">{t('orders.filters.allStatuses')}</option>
          {ORDER_STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{t(`status.order.${s}`)}</option>
          ))}
        </select>
        <select value={branchId} onChange={(e) => { setBranchId(e.target.value); setPage(1); }} className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm">
          <option value="">{t('orders.filters.allBranches')}</option>
          {branches.map((b) => (
            <option key={b._id} value={b._id}>{b.name}</option>
          ))}
        </select>
        <DatePicker id="order-from" label={t('common.from')} compact value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <DatePicker id="order-to" label={t('common.to')} compact value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
      </ListSearchToolbar>

      {isLoading && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
          <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
            <table className="w-full text-sm">
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <TableRowSkeleton key={i} cols={7} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isLoading && orders.length === 0 && (
        <EmptyState
          icon="📦"
          title={t('empty.noOrders')}
          subtitle={t('orders.marketplaceEmptySubtitle')}
        />
      )}

      {!isLoading && orders.length > 0 && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800">
          <ResponsiveTable
            columns={[
              { key: 'order', label: t('orders.table.orderNumber'), render: (o) => t('orders.drawer.orderNumber', { number: o._id.slice(-8).toUpperCase() }) },
              {
                key: 'client',
                label: t('orders.table.client'),
                render: (o) =>
                  o.clientId ? `${o.clientId.firstName} ${o.clientId.lastName}` : '—',
              },
              { key: 'items', label: t('orders.table.items'), render: (o) => o.items?.length },
              { key: 'total', label: t('orders.table.total'), render: (o) => t('common.currency', { amount: o.totalAmount?.toLocaleString() }) },
              {
                key: 'payment',
                label: t('orders.drawer.payment'),
                render: (o) => {
                  const pay = paymentBadge(o);
                  return <span className={pay.className}>{pay.label}</span>;
                },
              },
              {
                key: 'status',
                label: t('orders.table.status'),
                render: (o) => (
                  <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-xs">{t(`status.order.${o.status}`)}</span>
                ),
              },
              {
                key: 'date',
                label: t('orders.table.date'),
                render: (o) => new Date(o.createdAt).toLocaleDateString(),
              },
            ]}
            rows={orders.map((o) => ({ ...o, id: o._id }))}
            onRowClick={(row) => setSelectedId(row.id)}
          />
          <PaginatedFooter
            pagination={pagination}
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
          />
        </div>
      )}

      <OrderDetailDrawer
        orderId={selectedId}
        onClose={() => setSelectedId(null)}
        salonMode
      />
    </DashboardShell>
  );
};

export default SalonOrdersPage;
